import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, File, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  iniciarUploadAssincrono,
  verificarStatusUpload,
  obterResultadoUpload,
} from '../../servicos/servicoApiDocumentos';
import type { DocumentoAnexado } from '../../tipos/tiposCriacaoPeticao';
import type { RespostaResultadoUpload } from '../../tipos/tiposDocumentos';
import { formatarTamanhoArquivo } from '../../tipos/tiposDocumentos';
import { useModal } from '../../../../core/contextos/modalContext';

// Local constants per spec
const EXTENSOES_PERMITIDAS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.zip'];
import { TIPOS_MIME_ACEITOS } from '../../tipos/tiposDocumentos';
const TAMANHO_MAXIMO_POR_ARQUIVO_BYTES = 50 * 1024 * 1024; // 50MB
const TAMANHO_MAXIMO_TOTAL_BYTES = 200 * 1024 * 1024; // 200MB

// Interface de Props conforme demanda 003
interface PropsComponenteUploadDocumentos {
  peticaoId: string;
  documentosExistentes?: DocumentoAnexado[];
  onDocumentosConcluidos: (documentos: DocumentoAnexado[]) => void;
  onVoltar: () => void;
  onErro?: (erro: string) => void;
}

// Estado local do arquivo no componente (similar a ArquivoParaUpload)
interface EstadoDocumento {
  arquivo?: File; // pode ser undefined para documentos já existentes
  id: string; // id local temporário
  nome: string;
  tamanho: number;
  tipo: string;
  preview?: string;
  progresso: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  erro?: string;
  categoria?: 'comprovante' | 'contrato' | 'comunicacao' | 'outro';
  descricao?: string;
  idBackend?: string; // documento_id retornado pelo backend
  etapa?: string;
}

function gerarIdTemporario() {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
}

function obterExtensao(nome: string) {
  return nome.substring(nome.lastIndexOf('.')).toLowerCase();
}

export function ComponenteUploadDocumentos({
  peticaoId,
  documentosExistentes = [],
  onDocumentosConcluidos,
  onVoltar,
  onErro,
}: PropsComponenteUploadDocumentos): JSX.Element {
  const { mostrarAlerta, mostrarConfirmacao } = useModal();

  const [lista, setLista] = useState<EstadoDocumento[]>(() => {
    // Re-hidratar documentos existentes: não podem ser re-enviados
    return documentosExistentes.map((d) => ({
      arquivo: undefined,
      id: d.id,
      nome: d.nome,
      tamanho: d.tamanho,
      tipo: d.tipo,
      progresso: 100,
      status: 'completed',
      categoria: d.categoria,
      descricao: d.descricao,
      idBackend: d.id,
    }));
  });

  const [errosValidacao, setErrosValidacao] = useState<string[]>([]);
  const queueRef = useRef<string[]>([]); // fila de ids temporários para upload sequencial
  const uploadingRef = useRef<string | null>(null); // id do arquivo em envio
  const [isUploading, setIsUploading] = useState(false);

  // Valida arquivos antes de adicioná-los
  const validarArquivos = useCallback((arquivos: File[]): { aceitos: File[]; erros: string[] } => {
    const aceitos: File[] = [];
    const erros: string[] = [];

    const nomesAtuais = new Set(lista.map((d) => d.nome));
    let totalBytes = lista.reduce((acc, a) => acc + a.tamanho, 0);

    for (const arquivo of arquivos) {
      const nome = arquivo.name;
      const extensao = obterExtensao(nome);

      // extensão
      if (!EXTENSOES_PERMITIDAS.includes(extensao)) {
        erros.push(`${nome}: tipo de arquivo não suportado`);
        continue;
      }

      // tamanho individual
      if (arquivo.size > TAMANHO_MAXIMO_POR_ARQUIVO_BYTES) {
        erros.push(`${nome}: arquivo muito grande (máx ${TAMANHO_MAXIMO_POR_ARQUIVO_BYTES / (1024 * 1024)}MB)`);
        continue;
      }

      // duplicata
      if (nomesAtuais.has(nome)) {
        erros.push(`${nome}: documento duplicado`);
        continue;
      }

      // tamanho total
      if (totalBytes + arquivo.size > TAMANHO_MAXIMO_TOTAL_BYTES) {
        erros.push(`${nome}: excede tamanho total permitido (${TAMANHO_MAXIMO_TOTAL_BYTES / (1024 * 1024)}MB)`);
        continue;
      }

      totalBytes += arquivo.size;
      nomesAtuais.add(nome);
      aceitos.push(arquivo);
    }

    return { aceitos, erros };
  }, [lista]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setErrosValidacao([]);
    const { aceitos, erros } = validarArquivos(acceptedFiles);

    if (erros.length > 0) {
      setErrosValidacao(erros);
      mostrarAlerta?.({ titulo: 'Erro de validação', mensagem: erros.join('\n') });
    }

    if (aceitos.length > 0) {
      const novos = aceitos.map((f) => ({
        arquivo: f,
        id: gerarIdTemporario(),
        nome: f.name,
        tamanho: f.size,
        tipo: f.type || obterExtensao(f.name),
        progresso: 0,
        status: 'pending' as const,
      }));

      setLista((prev) => [...prev, ...novos]);
      // adiciona à fila
      novos.forEach((n) => queueRef.current.push(n.id));
    }
  }, [validarArquivos, mostrarAlerta]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: TIPOS_MIME_ACEITOS,
    multiple: true,
    maxSize: TAMANHO_MAXIMO_POR_ARQUIVO_BYTES,
  });

  // Remover arquivo
  const removerArquivo = (id: string) => {
    const arquivo = lista.find((l) => l.id === id);
    if (!arquivo) return;

    // Confirmar antes de remover: usar modal de confirmação com callback
    mostrarConfirmacao?.({
      titulo: 'Remover documento',
      mensagem: `Tem certeza que deseja remover "${arquivo.nome}"?`,
      aoConfirmar: () => {
        setLista((prev) => prev.filter((p) => p.id !== id));
        queueRef.current = queueRef.current.filter((qid) => qid !== id);
      },
      aoCancelar: () => {},
    });
  };

  // Atualizar meta dados (categoria/descricao)
  const atualizarMetadados = (id: string, data: Partial<EstadoDocumento>) => {
    setLista((prev) => prev.map((p) => (p.id === id ? { ...p, ...data } : p)));
  };

  // Função para processar o próximo item da fila em modo sequencial
  const processarProximo = useCallback(async () => {
    if (isUploading) return;
    const proximoId = queueRef.current.shift();
    if (!proximoId) return;

    const item = lista.find((l) => l.id === proximoId);
    if (!item || !item.arquivo) {
      // nada a enviar, processar o próximo
      processarProximo();
      return;
    }

    setIsUploading(true);
    uploadingRef.current = item.id;

    setLista((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'uploading', progresso: 0 } : p)));

    try {
      const inicio = await iniciarUploadAssincrono(item.arquivo, item.categoria, peticaoId, item.descricao);
      const uploadId = inicio.upload_id;

      // Poll sequentially até CONCLUIDO/ERRO
      let finalizou = false;
      while (!finalizou) {
        // esperar 1.5s entre chamadas (reduce polling frequency)
        await new Promise((r) => setTimeout(r, 1500));
        const status = await verificarStatusUpload(uploadId);
        const progresso = status.progresso_percentual ?? 0;
        const etapaAtual = status.etapa_atual ?? '';

        setLista((prev) => prev.map((p) => (p.id === item.id ? { ...p, progresso, erro: status.mensagem_erro, etapa: etapaAtual } : p)));

        if (status.status === 'CONCLUIDO') {
          finalizou = true;
          // obter resultado
          const resultado = await obterResultadoUpload(uploadId);
          // resultado pode ser único ou, no caso de ZIP, backend pode ter retornado objetos diferentes
          // Se possuir `documento_id` único -> mapear
          const anexados: DocumentoAnexado[] = [];

          if (Array.isArray(resultado as unknown) && (resultado as unknown as RespostaResultadoUpload[]).length > 0) {
            // Caso o backend retorne um array de resultados (ex: ZIP com múltiplos arquivos)
            const arr = resultado as unknown as RespostaResultadoUpload[];
            for (const r of arr) {
              anexados.push({
                id: r.documento_id || gerarIdTemporario(),
                nome: r.nome_arquivo || 'Documento',
                tipo: r.tipo_documento || '',
                tamanho: r.tamanho_bytes || 0,
                dataUpload: new Date().toISOString(),
                categoria: item.categoria,
                descricao: item.descricao,
              });
            }
          } else {
            const r = resultado as RespostaResultadoUpload;
            anexados.push({
              id: r.documento_id,
              nome: r.nome_arquivo || item.nome,
              tipo: r.tipo_documento || item.tipo,
              tamanho: r.tamanho_bytes || item.tamanho,
              dataUpload: new Date().toISOString(),
              categoria: item.categoria,
              descricao: item.descricao,
            });
          }

          // atualizar item com sucesso
          setLista((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'completed', progresso: 100, idBackend: anexados[0].id } : p)));

          // adicionar anexados como completos na lista (evitar duplicação do item enviado)
          setLista((prev) => {
            // manter os que já existem e adicionar novos (somente se zip retornou mais de 1)
            const idsExistentes = new Set(prev.map((x) => x.idBackend || x.id));
            const novos: EstadoDocumento[] = anexados
              .filter((a) => !idsExistentes.has(a.id))
              .map((a) => ({
                id: a.id,
                nome: a.nome,
                tamanho: a.tamanho,
                tipo: a.tipo,
                progresso: 100,
                status: 'completed',
                categoria: a.categoria,
                descricao: a.descricao,
                idBackend: a.id,
              }));
            return [...prev, ...novos];
          });

        } else if (status.status === 'ERRO') {
          finalizou = true;
          setLista((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'error', erro: status.mensagem_erro || 'Erro ao processar arquivo' } : p)));
          if (onErro) onErro(status.mensagem_erro || 'Erro durante upload');
          mostrarAlerta?.({ titulo: 'Erro de upload', mensagem: status.mensagem_erro || 'Erro ao processar arquivo' });
        }
      }

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro no upload';
      setLista((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'error', erro: msg } : p)));
      if (onErro) onErro(msg);
      mostrarAlerta?.({ titulo: 'Erro', mensagem: msg });
    } finally {
      uploadingRef.current = null;
      setIsUploading(false);
      // se ainda houver arquivos na fila, processar o próximo
      if (queueRef.current.length > 0) {
        processarProximo();
      }
    }
  }, [isUploading, lista, peticaoId, onErro, mostrarAlerta]);

  // Quando lista for atualizada e houver itens pendentes, iniciar processamento
  useEffect(() => {
    const pendentes = lista.filter((l) => l.status === 'pending');
    if (pendentes.length > 0 && !isUploading) {
      // se a fila estiver vazia, inicializar com os pendentes
      if (queueRef.current.length === 0) {
        pendentes.forEach((p) => queueRef.current.push(p.id));
      }
      processarProximo();
    }
  }, [lista, isUploading, processarProximo]);

  // Botões de navegação: Voltar / Continuar / Pular
  const handleVoltar = () => {
    onVoltar();
  };

  const handleContinuar = () => {
    // Verificar se pelo menos 1 documento com status completed
    const concluido = lista.filter((l) => l.status === 'completed');
    const emAndamento = lista.some((l) => l.status === 'uploading');
    if (concluido.length === 0) {
      mostrarAlerta?.({ titulo: 'Atenção', mensagem: 'Envie pelo menos 1 documento antes de continuar' });
      return;
    }
    if (emAndamento) {
      mostrarAlerta?.({ titulo: 'Aguarde', mensagem: 'Ainda há documentos sendo processados' });
      return;
    }

    // Mapear para DocumentoAnexado e chamar callback
    const documentos: DocumentoAnexado[] = lista.filter((l) => l.status === 'completed').map((l) => ({
      id: l.idBackend ?? l.id,
      nome: l.nome,
      tipo: l.tipo,
      tamanho: l.tamanho,
      dataUpload: new Date().toISOString(),
      categoria: l.categoria,
      descricao: l.descricao,
    }));

    onDocumentosConcluidos(documentos);
  };

  const handlePular = () => {
    mostrarConfirmacao?.({
      titulo: 'Pular esta etapa',
      mensagem: 'Tem certeza? Documentos ajudam muito na análise',
      aoConfirmar: () => onDocumentosConcluidos([]),
    });
  };

  const totalConcluidos = lista.filter((l) => l.status === 'completed').length;

  // UI Component for item
  function Item({ item }: { item: EstadoDocumento }) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            {item.status === 'completed' ? (
              <CheckCircle className="size-5 text-green-500" />
            ) : item.status === 'uploading' ? (
              <Loader2 className="size-5 animate-spin text-blue-500" />
            ) : item.status === 'error' ? (
              <AlertCircle className="size-5 text-red-500" />
            ) : (
              <File className="size-5 text-gray-400" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{item.nome}</p>
            <p className="text-xs text-gray-500">{formatarTamanhoArquivo(item.tamanho)}</p>
            {item.status === 'error' && <p className="text-xs text-red-600">{item.erro}</p>}
          </div>

          <div className="flex items-center gap-2">
            {/* category select */}
            <select
              value={item.categoria ?? ''}
              onChange={(e) => atualizarMetadados(item.id, { categoria: e.target.value as EstadoDocumento['categoria'] })}
              className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700"
              disabled={item.status === 'uploading'}
            >
              <option value="">Categoria</option>
              <option value="comprovante">Comprovante</option>
              <option value="contrato">Contrato</option>
              <option value="comunicacao">Comunicação</option>
              <option value="outro">Outro</option>
            </select>

            {/* descricao */}
            <input
              type="text"
              value={item.descricao ?? ''}
              onChange={(e) => atualizarMetadados(item.id, { descricao: e.target.value })}
              placeholder="Descrição (opcional)"
              className="w-40 rounded border border-gray-200 px-2 py-1 text-xs text-gray-700"
              disabled={item.status === 'uploading'}
            />

            {/* remove */}
            {item.status !== 'uploading' && (
              <button onClick={() => removerArquivo(item.id)} className="p-1 text-gray-400 hover:text-red-600" title="Remover">
                <X className="size-5" />
              </button>
            )}
          </div>
        </div>

        {item.status === 'uploading' && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>{item.progresso}%</span>
              <span className="text-xs text-gray-600">{item.progresso > 0 ? `${item.progresso}%` : 'Aguardando...'}</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
              <div style={{ width: `${item.progresso}%` }} className="h-2 rounded-full bg-blue-500 transition-all" />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="space-y-4">
        <div {...getRootProps()} className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}>
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            <Upload className={`size-16 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} />
            <div className="text-sm text-gray-700">{isDragActive ? 'Solte os arquivos aqui...' : 'Arraste arquivos aqui ou clique para selecionar'}</div>
            <div className="text-xs text-gray-500">Aceita: PDF, DOC, DOCX, JPG, PNG, ZIP (máx. 50MB por arquivo, total 200MB)</div>
          </div>
        </div>

        {errosValidacao.length > 0 && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {errosValidacao.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        {lista.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium">Documentos anexados ({lista.length})</h3>
              <div className="text-sm text-gray-500">Concluídos: {totalConcluidos}</div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {lista.map((it) => (
                <Item key={it.id} item={it} />
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => processarProximo()}
                disabled={isUploading}
                className={`rounded px-4 py-2 text-sm ${isUploading ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {isUploading ? <><Loader2 className="mr-2 size-4 animate-spin"/> Enviando...</> : 'Enviar documentos'}
              </button>

              <button
                onClick={() => {
                  setLista([]);
                  queueRef.current = [];
                }}
                disabled={isUploading}
                className={`rounded px-4 py-2 text-sm ${isUploading ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Limpar tudo
              </button>
            </div>
          </div>
        )}

        {/* Navegação do wizard */}
        <div className="flex items-center justify-between border-t pt-5">
          <div>
            <button onClick={handleVoltar} className="rounded bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">Voltar</button>
          </div>

          <div className="flex items-center gap-4">
            {lista.length === 0 && (
              <button onClick={handlePular} className="text-sm text-gray-500 underline hover:text-gray-700">Pular esta etapa</button>
            )}

            <button
              onClick={handleContinuar}
              disabled={isUploading || totalConcluidos === 0}
              className={`rounded px-4 py-2 text-sm ${isUploading || totalConcluidos === 0 ? 'bg-gray-300 text-gray-500' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComponenteUploadDocumentos;
